import React from 'react';
import {
    EditNode,
    EditNodeToolbar,
} from '@react-admin/ra-tree';
import {
    Edit,
    TabbedForm,
    FormTab,
    ReferenceField,
    TextInput,
    TextField,
    Toolbar,
    SaveButton,
    DeleteButton,
    useRecordContext,
    usePermissions,
    ReferenceManyField,
    Datagrid,
    Pagination,
    NumberInput,
    AutocompleteInput,
    ReferenceInput,
} from 'react-admin';
import { Card } from '@mui/material';
import PresentationDashboard from 'dashboard/PresentationDashboard';
import { Feature } from 'feature';
import { ClientContext, useClientContext } from 'contexts/client';

/**
 * Custom toolbar component for the edit form
 * Includes save and delete buttons with proper permission checks
 */
const EditToolbar = () => {
    const { permissions } = usePermissions();
    const isAdmin = permissions === 'site_admin' || permissions.endsWith('_admin');
    const toolBarStyle = {
        display: 'flex',
        justifyContent: 'space-between'
    };
    return (
        <Toolbar sx={toolBarStyle}>
            <SaveButton
                redirect="edit"
                disabled={!isAdmin}
            />
            {isAdmin && (
                <DeleteButton
                    label="Delete"
                    mutationMode="pessimistic"
                    confirmTitle="Delete Record"
                    confirmContent="Are you sure you want to delete this record? This action cannot be undone."
                    redirect="list"
                />
            )}
        </Toolbar>
    );
};

/**
 * Dashboard section component that uses the current record context
 */
const DashboardSection = () => {
    const record = useRecordContext();
    if (!record?.name) return null;

    return (
        <ClientContext.Consumer>
            {({state}) => {
                const { CLIENT } = state.client;
                return (
                    <PresentationDashboard 
                        client={CLIENT} 
                        deploy_target='business_unit' 
                        filter_by={record.name} 
                        filter_field={'business_unit@eq'}
                    />
                );
            }}
        </ClientContext.Consumer>
    );
};

const sortOrder = { field: 'name_path', order: 'ASC' };

/**
 * Main edit component for BusinessUnit
 * Uses modern React-Admin 5.8.4 patterns with hooks
 */
const BusinessUnitEdit = (props) => {
    const { permissions } = usePermissions();
    const isAdmin = permissions === 'site_admin';
    const { state: clientState } = useClientContext();

    const runtimeItemGroupFilterOptions = clientState?.client?.reportingOptions.runtimeItemGroupFilterOptions.filter(elm => elm.name.includes('business_unit')) || [];

    return (
        <ClientContext.Consumer>
            {({state}) => {
                const { CLIENT } = state.client;

                return (
                    <EditNode {...props}>
                        <Card>
                            <TabbedForm toolbar={<EditToolbar />}>
                                <FormTab label="Info">
                                    <Feature name="dashboard">
                                        <DashboardSection />
                                    </Feature>
                                </FormTab>
                                <FormTab label="Edit">
                                    <NumberInput source="id" />
                                    <TextInput source="text" />
                                    <ReferenceInput
                                        reference='kraftheinz_business_unit'
                                        source='parent_id'
                                        perPage={10}
                                        fullWidth
                                        sort={sortOrder}
                                        filterToQuery={searchText => ({ name_path:  searchText })}
                                    >
                                        <AutocompleteInput
                                            optionText='name_path'
                                        />
                                    </ReferenceInput>
                                </FormTab>
                                {runtimeItemGroupFilterOptions.map((elm, i) => {
                                    return (
                                        <FormTab label={(runtimeItemGroupFilterOptions.length > 1) ? `${elm.label} - Assigned Items` : 'Assignments'} key={elm.id}>
                                            <ReferenceManyField
                                                fullWidth
                                                label="Items In Business Unit"
                                                reference={`${CLIENT}_master_items`}
                                                target={elm.athena_column_name}
                                                perPage={100}
                                                pagination={<Pagination />}
                                            >
                                                <Datagrid>
                                                    <ReferenceField 
                                                        label="ID" 
                                                        source="infobate_master_item_id" 
                                                        reference={`${CLIENT}_master_items`}
                                                        allowEmpty 
                                                        link="edit"
                                                    >
                                                        <TextField source="id"/>
                                                    </ReferenceField>
                                                    <TextField label="upc" source="infobate_upc"/>
                                                    <TextField label="description" source="infobate_description"/>
                                                    <TextField label="measure" source="infobate_measure"/>
                                                    <TextField label="pack" source="infobate_pack"/>
                                                    <TextField label="manufacturer" source="infobate_manufacturer"/>
                                                </Datagrid>
                                            </ReferenceManyField>
                                        </FormTab>
                                    );
                                })}
                            </TabbedForm>
                        </Card>
                    </EditNode>
                );
            }}
        </ClientContext.Consumer>
    );
};

export default BusinessUnitEdit;